import { Link } from 'react-router-dom'
import type { CharacterWithStats } from '@shared/types'
import { CLASSES, type ClassKey } from '@shared/rules/constants'
import { formatModifier } from '@shared/rules/calc'
import { Badge, Panel } from './ui'
import { cn } from '@/lib/cn'

export function HealthBar({ current, max, temp = 0 }: { current: number; max: number; temp?: number }) {
  const ratio = max > 0 ? Math.max(0, Math.min(1, current / max)) : 0
  const tone =
    current <= 0 ? 'bg-ink-600'
    : ratio <= 0.25 ? 'bg-blood-500'
    : ratio <= 0.5 ? 'bg-gold-500'
    : 'bg-moss-500'

  return (
    <div
      role="meter"
      aria-valuenow={current}
      aria-valuemin={0}
      aria-valuemax={max}
      aria-label="Hit points"
      className="h-1.5 w-full overflow-hidden rounded-full bg-ink-750"
    >
      <div className={cn('h-full rounded-full transition-[width] duration-300', tone)} style={{ width: `${ratio * 100}%` }} />
      {temp > 0 && <span className="sr-only">plus {temp} temporary</span>}
    </div>
  )
}

export function PartyCard({ character }: { character: CharacterWithStats }) {
  const def = CLASSES[character.class as ClassKey]
  const down = character.hpCurrent <= 0

  return (
    <Link to={`/characters/${character.id}`} className="group">
      <Panel className={cn('panel-interactive h-full p-4', down && 'border-blood-500/40')}>
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h3 className="truncate font-display text-base leading-tight group-hover:text-gold-400">{character.name}</h3>
            <p className="mt-0.5 truncate text-xs text-ink-400">
              Level {character.level} {def?.name ?? character.class ?? 'Adventurer'}
              {character.subclass && ` · ${character.subclass}`}
            </p>
          </div>
          {down && <Badge tone="blood">Down</Badge>}
        </div>

        <div className="mt-3 space-y-1.5">
          <div className="flex items-baseline justify-between font-mono text-xs">
            <span className={cn('tabular-nums', down ? 'text-blood-500' : 'text-ink-300')}>
              {character.hpCurrent}<span className="text-ink-500">/{character.hpMax}</span>
              {character.hpTemp > 0 && <span className="text-moss-500"> +{character.hpTemp}</span>}
            </span>
            <span className="text-ink-500">HP</span>
          </div>
          <HealthBar current={character.hpCurrent} max={character.hpMax} temp={character.hpTemp} />
        </div>

        <dl className="mt-3 grid grid-cols-3 gap-2 border-t border-ink-700/60 pt-3 text-center">
          <Stat label="AC" value={character.derived.armorClass} />
          <Stat label="Init" value={formatModifier(character.derived.initiative)} />
          <Stat label="Pass. Per" value={character.derived.passivePerception} />
        </dl>

        {character.ownerName && (
          <p className="mt-3 truncate text-[11px] text-ink-500">Played by {character.ownerName}</p>
        )}
      </Panel>
    </Link>
  )
}

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div>
      <dd className="font-mono text-sm tabular-nums text-ink-100">{value}</dd>
      <dt className="text-[10px] uppercase tracking-wider text-ink-500">{label}</dt>
    </div>
  )
}
